import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../services/api";
import type { Conversation } from "../../../types/conversation";
import type { PendingConversation } from "../../../types/pending-conversation";
import type { Message } from "../../../types/message";
import { useAuth } from "../../../hooks/use-auth";
import { SOCKET_EVENTS } from "../../../services/socket/events";
import socket from "../../../services/socket";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { getApiErrorMessage, getMessageTypeFromMime } from "../../../utils";
import { useToast } from "../../../contexts/toast/toast-context";
import { useSocket } from "../../../hooks/use-socket";
import { ConversationSidebar } from "../../../components/conversation/sidebar";
import { ConversationHeader } from "../../../components/conversation/header";
import { MessageList } from "../../../components/conversation/message-list";
import { MessageInput } from "../../../components/conversation/message-input";
import { ConversationScheduleModal } from "../../../components/appointment/conversation-schedule-modal";
import { ConversationProtocolModal } from "../../../components/protocol/conversation-protocol-modal";
import {
  ConversationTemplateModal,
  type TemplateOption as ConversationTemplateOption,
} from "../../../components/conversation/template-modal";
import type { AttendantSentMessageDto } from "../../../types/dto/attendant-sent-message-dto";
import type { ConversationContextDto } from "../../../types/dto/conversation-context-dto";
import type { ConversationHistoryDto } from "../../../types/dto/conversation-history-dto";
import { ArrowLeft } from "lucide-react";

type UploadFileDto = {
  url: string;
  fileKey: string;
  mimeType: string;
};

const WHATSAPP_SESSION_WINDOW_MS = 24 * 60 * 60 * 1000;
const AUDIO_OGG_OPUS_MIME_TYPE = "audio/ogg;codecs=opus";
const NEW_CONVERSATION_AUDIO_SRC = "/audio/new-conversation.mp3";
const NEW_MESSAGE_AUDIO_SRC = "/audio/new-message.mp3";
const CONVERSATION_HISTORY_PAGE_SIZE = 3;

const resolveTemplateParamCount = (
  template?: ConversationTemplateOption,
): number => {
  const parsed = Number(template?.params_count);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
};

const resolveMessageCreatedAtKey = (message: Message): string => {
  if (!message.createdAt) return "";
  const parsedDate = new Date(message.createdAt);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(message.createdAt);
  }
  return parsedDate.toISOString();
};

const buildMessageIdentity = (message: Message): string => {
  if (message.id) {
    return `id:${message.id}`;
  }

  if (message.brokerId) {
    return `broker:${message.brokerId}`;
  }

  return [
    message.role,
    message.type,
    message.content ?? "",
    message.url ?? "",
    message.contentType ?? "",
    message.fileName ?? "",
    message.templateId ?? "",
    resolveMessageCreatedAtKey(message),
  ].join("|");
};

const appendUniqueMessages = (
  currentMessages: Message[],
  incomingMessages: Message[],
): Message[] => {
  if (incomingMessages.length === 0) return currentMessages;

  const merged = [...currentMessages];
  const identities = new Set(
    merged.map((message) => buildMessageIdentity(message)),
  );

  incomingMessages.forEach((message) => {
    const identity = buildMessageIdentity(message);
    if (identities.has(identity)) {
      return;
    }

    identities.add(identity);
    merged.push(message);
  });

  return merged;
};

export default function Conversas() {
  const { user } = useAuth();
  const { error, info, success } = useToast();

  const markAsReadInFlightRef = useRef<Set<string>>(new Set());
  const conversationContextRequestRef = useRef(0);
  const conversationHistoryRequestRef = useRef(0);
  const lastSocketErrorAtRef = useRef(0);
  const newConversationAudioRef = useRef<HTMLAudioElement | null>(null);
  const newMessageAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingConversationIdsRef = useRef<Set<string>>(new Set());

  const [abaAtiva, setAbaAtiva] = useState<"em_espera" | "em_atendimento">(
    "em_atendimento",
  );
  const [modoSupervisao, setModoSupervisao] = useState(false);

  const [pendingConversations, setPendingConversations] = useState<
    PendingConversation[]
  >([]);
  const [conversations, setConversations] = useState<PendingConversation[]>([]);
  const [supervisionConversations, setSupervisionConversations] = useState<
    PendingConversation[]
  >([]);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(max-width: 768px)").matches;
  });

  const canSupervise = true;
  const [conversaSelecionada, setConversaSelecionada] =
    useState<Conversation | null>(null);
  const [conversasAnteriores, setConversasAnteriores] = useState<
    Conversation[]
  >([]);
  const [hasMoreConversationHistory, setHasMoreConversationHistory] =
    useState(false);
  const [isConversationHistoryLoading, setIsConversationHistoryLoading] =
    useState(false);
  const [novaMensagem, setNovaMensagem] = useState("");

  const [modalEncerrarAberto, setModalEncerrarAberto] = useState(false);
  const [modalBloquearAberto, setModalBloquearAberto] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isReturningToQueue, setIsReturningToQueue] = useState(false);
  const [isReturningToAi, setIsReturningToAi] = useState(false);
  const [isAssumingConversation, setIsAssumingConversation] = useState(false);
  const [isSupervisionLoading, setIsSupervisionLoading] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const [isProtocolModalOpen, setIsProtocolModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateOptions, setTemplateOptions] = useState<
    ConversationTemplateOption[]
  >([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isSendingTemplate, setIsSendingTemplate] = useState(false);

  const lastUserMessageAt = useMemo(() => {
    const messages = conversaSelecionada?.messages ?? [];

    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message.role !== "user") {
        continue;
      }

      const messageDate = message.createdAt ?? message.updatedAt;
      if (!messageDate) continue;

      const parsedDate = new Date(messageDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }

    return null;
  }, [conversaSelecionada?.messages]);

  const isWhatsappWindowExpired = useMemo(() => {
    if (!lastUserMessageAt) return false;
    return (
      Date.now() - lastUserMessageAt.getTime() > WHATSAPP_SESSION_WINDOW_MS
    );
  }, [lastUserMessageAt]);

  const whatsappWindowReason = isWhatsappWindowExpired
    ? "Janela de 24h encerrada. Aguarde o contato enviar nova mensagem para reabrir a sessão."
    : undefined;

  const canAssumeConversation =
    !!conversaSelecionada &&
    modoSupervisao &&
    conversaSelecionada.status === "AI_ATTENDANCE";

  const canReturnConversationToAi = useMemo(() => {
    if (!conversaSelecionada || modoSupervisao) return false;
    if (conversaSelecionada.status !== "HUMAN_ATTENDANCE") return false;
    return conversaSelecionada.firstContactByAi === true;
  }, [conversaSelecionada, modoSupervisao]);

  const selectedTemplate = useMemo(
    () =>
      templateOptions.find((template) => template.id === selectedTemplateId),
    [selectedTemplateId, templateOptions],
  );

  const playNotificationSound = useCallback(
    (audioRef: { current: HTMLAudioElement | null }) => {
      const audio = audioRef.current;
      if (!audio) return;

      audio.currentTime = 0;
      void audio.play().catch(() => {});
    },
    [],
  );

  const conversationRoomKey = useMemo(() => {
    const ids = new Set<string>();

    conversations.forEach((conversation) =>
      ids.add(conversation.conversationId),
    );

    if (modoSupervisao) {
      supervisionConversations.forEach((conversation) =>
        ids.add(conversation.conversationId),
      );
    }

    if (modoSupervisao && conversaSelecionada?.id) {
      ids.add(conversaSelecionada.id);
    }

    return Array.from(ids).sort().join("|");
  }, [
    conversations,
    supervisionConversations,
    modoSupervisao,
    conversaSelecionada?.id,
  ]);

  const conversationRoomIds = useMemo(() => {
    if (!conversationRoomKey) return [];
    return conversationRoomKey.split("|");
  }, [conversationRoomKey]);

  useEffect(() => {
    getPendingConversations();
    getInAttendanceConversations();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const media = window.matchMedia("(max-width: 768px)");
    const handleChange = () => setIsMobile(media.matches);

    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
    } else {
      media.addListener(handleChange);
    }

    return () => {
      if (media.addEventListener) {
        media.removeEventListener("change", handleChange);
      } else {
        media.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    pendingConversationIdsRef.current = new Set(
      pendingConversations.map((conversation) => conversation.conversationId),
    );
  }, [pendingConversations]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    newConversationAudioRef.current = new Audio(NEW_CONVERSATION_AUDIO_SRC);
    newMessageAudioRef.current = new Audio(NEW_MESSAGE_AUDIO_SRC);

    return () => {
      newConversationAudioRef.current = null;
      newMessageAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    setIsSchedulingOpen(false);
    setIsTemplateModalOpen(false);
    setSelectedTemplateId("");
  }, [conversaSelecionada?.id]);

  useEffect(() => {
    if (!isTemplateModalOpen || !conversaSelecionada?.tenant?.appName) {
      return;
    }

    let cancelled = false;

    const loadTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const response = await api.get<ConversationTemplateOption[]>(
          `/templates/${conversaSelecionada.tenant.appName}`,
        );

        if (!cancelled) {
          setTemplateOptions(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          setTemplateOptions([]);
          const apiMessage = getApiErrorMessage(err);
          error(apiMessage);
        }
      } finally {
        if (!cancelled) {
          setTemplatesLoading(false);
        }
      }
    };

    loadTemplates();

    return () => {
      cancelled = true;
    };
  }, [error, isTemplateModalOpen, conversaSelecionada?.tenant?.appName]);

  const updateConversationTimestamp = (
    conversationId: string,
    timestamp?: string | Date,
  ) => {
    const newTimestamp =
      timestamp instanceof Date
        ? timestamp.toISOString()
        : (timestamp ?? new Date().toISOString());

    setConversations((prev) =>
      prev.map((c) =>
        c.conversationId === conversationId
          ? { ...c, timestamp: newTimestamp }
          : c,
      ),
    );

    setPendingConversations((prev) =>
      prev.map((c) =>
        c.conversationId === conversationId
          ? { ...c, timestamp: newTimestamp }
          : c,
      ),
    );

    setSupervisionConversations((prev) =>
      prev.map((c) =>
        c.conversationId === conversationId
          ? { ...c, timestamp: newTimestamp }
          : c,
      ),
    );
  };

  const clearUnreadFlagLocally = (conversationId: string) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.conversationId === conversationId ? { ...c, hasUnread: false } : c,
      ),
    );

    setSupervisionConversations((prev) =>
      prev.map((c) =>
        c.conversationId === conversationId ? { ...c, hasUnread: false } : c,
      ),
    );
  };

  async function markConversationAsRead(conversationId: string) {
    if (!conversationId || markAsReadInFlightRef.current.has(conversationId)) {
      return;
    }

    markAsReadInFlightRef.current.add(conversationId);
    try {
      await api.post(`/conversation/mark-as-read`, { conversationId });
    } catch {
      // estado local já foi atualizado; a sincronização volta no próximo refresh
    } finally {
      markAsReadInFlightRef.current.delete(conversationId);
    }
  }

  function clearSelectedConversation() {
    conversationContextRequestRef.current += 1;
    conversationHistoryRequestRef.current += 1;
    setConversaSelecionada(null);
    setConversasAnteriores([]);
    setHasMoreConversationHistory(false);
    setIsConversationHistoryLoading(false);
  }

  async function loadConversationContext(
    conversationId: string,
    fallbackCurrentConversation?: Conversation,
  ) {
    const requestId = conversationContextRequestRef.current + 1;
    conversationContextRequestRef.current = requestId;
    conversationHistoryRequestRef.current += 1;
    setIsConversationHistoryLoading(false);
    if (conversaSelecionada?.id !== conversationId) {
      setConversasAnteriores([]);
      setHasMoreConversationHistory(false);
    }

    try {
      const response = await api.get<ConversationContextDto>(
        `/conversation/get-context-by-id/${conversationId}`,
      );

      if (conversationContextRequestRef.current !== requestId) {
        return;
      }

      setConversaSelecionada(response.data.current);
      setConversasAnteriores(
        response.data.lastClosed ? [response.data.lastClosed] : [],
      );
      setHasMoreConversationHistory(!!response.data.lastClosed);
    } catch (err) {
      if (conversationContextRequestRef.current !== requestId) {
        return;
      }

      if (fallbackCurrentConversation) {
        setConversaSelecionada(fallbackCurrentConversation);
        setConversasAnteriores([]);
        setHasMoreConversationHistory(false);
      }

      throw err;
    }
  }

  const loadPreviousConversations = useCallback(async () => {
    const conversationId = conversaSelecionada?.id;
    if (
      !conversationId ||
      isConversationHistoryLoading ||
      !hasMoreConversationHistory
    ) {
      return;
    }

    const requestId = conversationHistoryRequestRef.current + 1;
    conversationHistoryRequestRef.current = requestId;
    setIsConversationHistoryLoading(true);

    try {
      const response = await api.get<ConversationHistoryDto>(
        `/conversation/get-history-by-id/${conversationId}`,
        {
          params: {
            offset: conversasAnteriores.length,
            limit: CONVERSATION_HISTORY_PAGE_SIZE,
          },
        },
      );

      if (conversationHistoryRequestRef.current !== requestId) return;

      setConversasAnteriores((current) => {
        const knownIds = new Set(
          current.map((conversation) => conversation.id),
        );
        const newConversations = response.data.data.filter(
          (conversation) => !knownIds.has(conversation.id),
        );
        return [...current, ...newConversations];
      });
      setHasMoreConversationHistory(response.data.hasMore);
    } catch (err) {
      if (conversationHistoryRequestRef.current === requestId) {
        error(getApiErrorMessage(err));
      }
    } finally {
      if (conversationHistoryRequestRef.current === requestId) {
        setIsConversationHistoryLoading(false);
      }
    }
  }, [
    conversaSelecionada?.id,
    conversasAnteriores.length,
    error,
    hasMoreConversationHistory,
    isConversationHistoryLoading,
  ]);

  async function takeConversation(pendingConversation: PendingConversation) {
    try {
      const response = await api.post<Conversation>(`/conversation/take`, {
        conversationId: pendingConversation.conversationId,
      });

      setPendingConversations((prev) =>
        prev.filter(
          (c) => c.conversationId !== pendingConversation.conversationId,
        ),
      );

      setConversations((prev) => [...prev, pendingConversation]);

      await loadConversationContext(response.data.id, response.data);
      setAbaAtiva("em_atendimento");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  function handleSelectConversation(conversation: PendingConversation) {
    if (modoSupervisao) {
      void openConversation(conversation);
      return;
    }

    if (abaAtiva === "em_espera") {
      void takeConversation(conversation);
      return;
    }

    void openConversation(conversation);
  }

  async function openConversation(pendingConversation: PendingConversation) {
    clearUnreadFlagLocally(pendingConversation.conversationId);

    try {
      await loadConversationContext(pendingConversation.conversationId);

      if (!modoSupervisao) {
        void markConversationAsRead(pendingConversation.conversationId);
      }
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  async function assumirConversa() {
    if (!conversaSelecionada || !canAssumeConversation || !user) return;
    if (isAssumingConversation) return;

    try {
      setIsAssumingConversation(true);
      const response = await api.post<Conversation>(`/conversation/take`, {
        conversationId: conversaSelecionada.id,
      });

      const updatedConversation = response.data as Conversation;

      setSupervisionConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversaSelecionada.id),
      );

      setConversations((prev) => {
        if (prev.some((c) => c.conversationId === conversaSelecionada.id)) {
          return prev;
        }

        const supervisionSnapshot = supervisionConversations.find(
          (c) => c.conversationId === conversaSelecionada.id,
        );

        const timestamp =
          supervisionSnapshot?.timestamp ?? new Date().toISOString();

        const nextConversation: PendingConversation = {
          tenantId:
            updatedConversation.tenant?.id ??
            supervisionSnapshot?.tenantId ??
            user.tenantId,
          conversationId: updatedConversation.id,
          userId:
            updatedConversation.user?.id ?? supervisionSnapshot?.userId ?? "",
          userPhone:
            updatedConversation.user?.userPhone ??
            supervisionSnapshot?.userPhone ??
            "",
          appName:
            updatedConversation.tenant?.appName ??
            supervisionSnapshot?.appName ??
            user.appName,
          userName:
            updatedConversation.user?.userName ??
            supervisionSnapshot?.userName ??
            "",
          userColor:
            updatedConversation.user?.color ?? supervisionSnapshot?.userColor,
          timestamp,
          status: "HUMAN_ATTENDANCE",
          hasUnread: false,
        };

        return [...prev, nextConversation];
      });

      await loadConversationContext(
        updatedConversation.id,
        updatedConversation,
      );
      setAbaAtiva("em_atendimento");
      setModoSupervisao(false);
      success("Conversa assumida com sucesso.");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsAssumingConversation(false);
    }
  }

  const handleEnviar = () => {
    if (modoSupervisao || isWhatsappWindowExpired) return;
    if (!novaMensagem.trim() || !conversaSelecionada) return;

    const message: Message = {
      type: "text",
      content: novaMensagem,
      role: "attendant",
      createdAt: new Date(),
    };

    const dto: AttendantSentMessageDto = {
      conversationId: conversaSelecionada.id,
      message,
      attendantId: user?.id,
      tenantId: user.tenantId,
    };

    socket.emit(SOCKET_EVENTS.ATTENDANT_SENT_MESSAGE, dto);

    setConversaSelecionada({
      ...conversaSelecionada,
      messages: [...conversaSelecionada.messages, message],
    });

    updateConversationTimestamp(conversaSelecionada.id, message.createdAt);

    setNovaMensagem("");
  };

  const canSendEstablishmentLocation = useMemo(() => {
    const tenant = conversaSelecionada?.tenant;
    return Boolean(
      Number.isFinite(tenant?.establishmentLatitude) &&
      Number.isFinite(tenant.establishmentLongitude) &&
      tenant.establishmentLocationName?.trim() &&
      tenant.establishmentAddress?.trim(),
    );
  }, [conversaSelecionada?.tenant]);

  const handleEnviarLocalizacao = () => {
    if (
      modoSupervisao ||
      isWhatsappWindowExpired ||
      !conversaSelecionada ||
      !canSendEstablishmentLocation
    ) {
      return;
    }

    const tenant = conversaSelecionada.tenant;
    const message: Message = {
      type: "location",
      role: "attendant",
      latitude: tenant.establishmentLatitude,
      longitude: tenant.establishmentLongitude,
      locationName: tenant.establishmentLocationName,
      address: tenant.establishmentAddress,
      createdAt: new Date(),
    };

    socket.emit(SOCKET_EVENTS.ATTENDANT_SENT_MESSAGE, {
      conversationId: conversaSelecionada.id,
      message,
      attendantId: user?.id,
      tenantId: user.tenantId,
    } satisfies AttendantSentMessageDto);

    setConversaSelecionada({
      ...conversaSelecionada,
      messages: [...conversaSelecionada.messages, message],
    });
    updateConversationTimestamp(conversaSelecionada.id, message.createdAt);
  };

  const openTemplateModal = () => {
    if (modoSupervisao || !conversaSelecionada) return;
    setSelectedTemplateId("");
    setIsTemplateModalOpen(true);
  };

  const closeTemplateModal = () => {
    if (isSendingTemplate) return;
    setIsTemplateModalOpen(false);
    setSelectedTemplateId("");
  };

  async function sendSelectedTemplate() {
    if (!conversaSelecionada) return;
    if (!selectedTemplate) {
      info("Selecione um template para envio.");
      return;
    }

    const templateParamsCount = resolveTemplateParamCount(selectedTemplate);
    if (templateParamsCount > 0) {
      info(
        "Templates com parâmetros não podem ser enviados por essa tela no momento.",
      );
      return;
    }

    const templateText = selectedTemplate.data?.trim() ?? "";
    if (!templateText) {
      info("O template selecionado não possui texto para envio.");
      return;
    }

    try {
      setIsSendingTemplate(true);

      const response = await api.post<Message>(`/conversation/send-template`, {
        conversationId: conversaSelecionada.id,
        templateId: selectedTemplate.id,
        templateText,
      });

      const sentMessage = response.data;

      setConversaSelecionada((prev) => {
        if (!prev || prev.id !== conversaSelecionada.id) return prev;
        return {
          ...prev,
          messages: appendUniqueMessages(prev.messages, [sentMessage]),
        };
      });

      updateConversationTimestamp(
        conversaSelecionada.id,
        sentMessage.createdAt,
      );
      success("Template enviado com sucesso.");
      setIsTemplateModalOpen(false);
      setSelectedTemplateId("");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsSendingTemplate(false);
    }
  }

  async function getPendingConversations() {
    try {
      const response = await api.get(`conversation/pending-conversations`);

      setPendingConversations(
        response.data.filter(
          (c: PendingConversation) => c.status === "WAITING_HUMAN_ATTENDANCE",
        ),
      );
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  async function getInAttendanceConversations() {
    try {
      const response = await api.get(
        `conversation/in-attendance-conversations`,
      );

      setConversations(response.data);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  async function getTenantInAttendanceConversations() {
    try {
      const response = await api.get(
        `conversation/tenant/in-attendance-conversations`,
      );

      setSupervisionConversations(response.data);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  const resyncAfterReconnect = useCallback(async () => {
    await Promise.all([
      getPendingConversations(),
      getInAttendanceConversations(),
      modoSupervisao ? getTenantInAttendanceConversations() : Promise.resolve(),
    ]);

    if (conversaSelecionada?.id) {
      try {
        await loadConversationContext(conversaSelecionada.id);
      } catch {
        // Falha de contexto já é tratada via toast dentro da chamada.
      }
    }
  }, [modoSupervisao, conversaSelecionada?.id]);

  const toggleSupervision = async () => {
    if (!canSupervise || isSupervisionLoading) return;

    try {
      setIsSupervisionLoading(true);
      if (!modoSupervisao) {
        await getTenantInAttendanceConversations();
      }

      clearSelectedConversation();
      setAbaAtiva("em_atendimento");
      setModoSupervisao((prev) => !prev);
    } finally {
      setIsSupervisionLoading(false);
    }
  };

  async function encerrarConversa() {
    if (!conversaSelecionada) return;
    if (isClosing) return;

    try {
      setIsClosing(true);
      await api.delete(`/conversation/close/${conversaSelecionada.id}`);
      setConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversaSelecionada.id),
      );
      clearSelectedConversation();
      setModalEncerrarAberto(false);
      success(`Conversa encerrada!`);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
      setModalEncerrarAberto(false);
    } finally {
      setIsClosing(false);
    }
  }

  async function devolverConversaParaFila() {
    if (!conversaSelecionada?.id) return;
    if (isReturningToQueue) return;

    try {
      setIsReturningToQueue(true);
      await api.post(`/conversation/return-to-queue`, {
        conversationId: conversaSelecionada.id,
      });

      setConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversaSelecionada.id),
      );
      clearSelectedConversation();
      setAbaAtiva("em_espera");
      success("Conversa devolvida para a fila.");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsReturningToQueue(false);
    }
  }

  async function devolverConversaParaIa() {
    if (!conversaSelecionada?.id || !canReturnConversationToAi) return;
    if (isReturningToAi) return;

    try {
      setIsReturningToAi(true);
      await api.post(`/conversation/return-to-ai`, {
        conversationId: conversaSelecionada.id,
      });

      setConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversaSelecionada.id),
      );
      clearSelectedConversation();
      success("Conversa devolvida para IA.");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsReturningToAi(false);
    }
  }

  async function bloquearContato() {
    if (!conversaSelecionada?.id) return;
    if (isBlocking) return;

    try {
      setIsBlocking(true);
      await api.post(`/blocked-contact`, {
        countryCode: conversaSelecionada.user.countryCode,
        dialCode: conversaSelecionada.user.dialCode,
        userName: conversaSelecionada.user.userName,
      });
      await api.delete(`/conversation/close/${conversaSelecionada.id}`);

      success("Contato bloqueado. Novas mensagens serão ignoradas.");

      setPendingConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversaSelecionada.id),
      );
      setConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversaSelecionada.id),
      );
      clearSelectedConversation();
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setModalBloquearAberto(false);
      setIsBlocking(false);
    }
  }

  async function handleUploadArquivo(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = event.target.files?.[0];
    if (
      !file ||
      !conversaSelecionada ||
      !user ||
      modoSupervisao ||
      isWhatsappWindowExpired
    )
      return;

    if (file.size > 20 * 1024 * 1024) {
      error(`"${file.name}" excede o limite de 20 MB.`);
      event.target.value = "";
      return;
    }

    try {
      setIsUploadingFile(true);
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await api.post<UploadFileDto>(
        "/conversation/upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const { url, fileKey, mimeType } = uploadResponse.data;

      const message: Message = {
        type: getMessageTypeFromMime(mimeType),
        url: url,
        role: "attendant",
        fileName: fileKey,
        contentType: mimeType,
        createdAt: new Date(),
      };

      const dto: AttendantSentMessageDto = {
        conversationId: conversaSelecionada.id,
        message,
        attendantId: user.id,
        tenantId: user.tenantId,
      };

      socket.emit(SOCKET_EVENTS.ATTENDANT_SENT_MESSAGE, dto);

      setConversaSelecionada({
        ...conversaSelecionada,
        messages: [...conversaSelecionada.messages, message],
      });

      updateConversationTimestamp(conversaSelecionada.id, message.createdAt);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsUploadingFile(false);
      event.target.value = "";
    }
  }

  function buildAudioFileName() {
    return `audio-${Date.now()}.ogg`;
  }

  async function handleEnviarAudio(audioBlob: Blob) {
    if (
      !audioBlob ||
      !conversaSelecionada ||
      !user ||
      modoSupervisao ||
      isWhatsappWindowExpired
    )
      return;

    try {
      const normalizedAudioBlob = new Blob([audioBlob], {
        type: AUDIO_OGG_OPUS_MIME_TYPE,
      });
      const file = new File([normalizedAudioBlob], buildAudioFileName(), {
        type: AUDIO_OGG_OPUS_MIME_TYPE,
      });

      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await api.post<UploadFileDto>(
        "/conversation/upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const { url, fileKey, mimeType } = uploadResponse.data;

      const message: Message = {
        type: getMessageTypeFromMime(mimeType),
        url: url,
        role: "attendant",
        fileName: fileKey,
        contentType: mimeType,
        createdAt: new Date(),
      };

      const dto: AttendantSentMessageDto = {
        conversationId: conversaSelecionada.id,
        message,
        attendantId: user.id,
        tenantId: user.tenantId,
      };

      socket.emit(SOCKET_EVENTS.ATTENDANT_SENT_MESSAGE, dto);

      setConversaSelecionada({
        ...conversaSelecionada,
        messages: [...conversaSelecionada.messages, message],
      });

      updateConversationTimestamp(conversaSelecionada.id, message.createdAt);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  useSocket({
    tenantId: user.tenantId,
    userId: user.id,
    conversationIds: conversationRoomIds,
    onReconnect: resyncAfterReconnect,
    onConnectError: (message) => {
      const now = Date.now();
      const minIntervalMs = 5000;
      if (now - lastSocketErrorAtRef.current < minIntervalMs) {
        return;
      }

      lastSocketErrorAtRef.current = now;
      info(`Realtime: ${message}`);
    },
    onPendingConversation: (conversation) => {
      const isNewPendingConversation = !pendingConversationIdsRef.current.has(
        conversation.conversationId,
      );

      if (isNewPendingConversation) {
        pendingConversationIdsRef.current.add(conversation.conversationId);
        playNotificationSound(newConversationAudioRef);
      }

      setPendingConversations((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.conversationId === conversation.conversationId,
        );

        if (existingIndex === -1) {
          return [...prev, conversation];
        }

        return prev.map((item) =>
          item.conversationId === conversation.conversationId
            ? { ...item, ...conversation }
            : item,
        );
      });

      info(`Novo atendimento aguardando na fila`);
    },
    onAiAttendanceConversation: (conversation) => {
      if (!modoSupervisao) return;

      const isSelected =
        conversaSelecionada?.id === conversation.conversationId;
      const nextConversation = {
        ...conversation,
        hasUnread: !isSelected,
      };

      setSupervisionConversations((prev) => {
        if (
          prev.some(
            (item) => item.conversationId === conversation.conversationId,
          )
        ) {
          return prev;
        }
        return [...prev, nextConversation];
      });
    },
    onTakenConversation: (conversationId) =>
      setPendingConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversationId),
      ),
    onConversationClosed: (conversationId) => {
      setPendingConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversationId),
      );
      setConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversationId),
      );
      setSupervisionConversations((prev) =>
        prev.filter((c) => c.conversationId !== conversationId),
      );
      if (conversaSelecionada?.id === conversationId) {
        clearSelectedConversation();
      }
    },
    onUserSentMessages: (conversationId, messages) => {
      if (messages.length === 0) return;
      playNotificationSound(newMessageAudioRef);

      const isSelected = conversaSelecionada?.id === conversationId;

      setConversaSelecionada((prev) => {
        if (!prev) return prev;
        if (prev.id === conversationId) {
          return {
            ...prev,
            messages: appendUniqueMessages(prev.messages, messages),
          };
        }
        return prev;
      });

      setConversations((prev) =>
        prev.map((c) => {
          if (c.conversationId === conversationId) {
            return {
              ...c,
              hasUnread: !isSelected,
            };
          } else {
            return c;
          }
        }),
      );

      setSupervisionConversations((prev) =>
        prev.map((c) => {
          if (c.conversationId === conversationId) {
            return {
              ...c,
              hasUnread: !isSelected,
            };
          } else {
            return c;
          }
        }),
      );

      const lastMessage = messages[messages.length - 1];
      updateConversationTimestamp(conversationId, lastMessage?.createdAt);

      if (isSelected && !modoSupervisao) {
        void markConversationAsRead(conversationId);
      }
    },
    onAttendantMessages: (
      conversationId,
      messages,
      _senderId,
      senderSocketId,
    ) => {
      if (messages.length === 0) return;
      if (senderSocketId && senderSocketId === socket.id) {
        return;
      }
      playNotificationSound(newMessageAudioRef);

      const isSelected = conversaSelecionada?.id === conversationId;

      setConversaSelecionada((prev) => {
        if (!prev) return prev;
        if (prev.id === conversationId) {
          return {
            ...prev,
            messages: appendUniqueMessages(prev.messages, messages),
          };
        }
        return prev;
      });

      setConversations((prev) =>
        prev.map((c) => {
          if (c.conversationId === conversationId) {
            return {
              ...c,
              hasUnread: !isSelected,
            };
          } else {
            return c;
          }
        }),
      );

      setSupervisionConversations((prev) =>
        prev.map((c) => {
          if (c.conversationId === conversationId) {
            return {
              ...c,
              hasUnread: !isSelected,
            };
          } else {
            return c;
          }
        }),
      );

      const lastMessage = messages[messages.length - 1];
      updateConversationTimestamp(conversationId, lastMessage?.createdAt);
    },
  });

  const shouldShowSidebar = !isMobile || !conversaSelecionada;
  const shouldShowConversation = !isMobile || !!conversaSelecionada;
  const canScheduleAppointment =
    !!conversaSelecionada?.tenant?.enabledScheduling &&
    !modoSupervisao &&
    !!conversaSelecionada?.user?.id;

  return (
    <div className="flex h-[90vh] min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft md:flex-row">
      {/* Sidebar */}
      {shouldShowSidebar && (
        <ConversationSidebar
          abaAtiva={abaAtiva}
          setAbaAtiva={setAbaAtiva}
          supervisionMode={modoSupervisao}
          canSupervise={canSupervise}
          onToggleSupervision={toggleSupervision}
          supervisionLoading={isSupervisionLoading}
          conversations={
            modoSupervisao ? supervisionConversations : conversations
          }
          pendingConversations={modoSupervisao ? [] : pendingConversations}
          onSelect={handleSelectConversation}
        />
      )}

      {/* Área de mensagens */}
      {shouldShowConversation && (
        <div className="flex min-h-0 flex-1 flex-col border-t border-slate-200 md:border-l md:border-t-0">
          {conversaSelecionada ? (
            <>
              {isMobile && (
                <div className="border-b bg-white px-4 py-2">
                  <button
                    type="button"
                    onClick={() => clearSelectedConversation()}
                    className="btn btn-ghost !px-2 !py-1"
                  >
                    <ArrowLeft className="w-6 h-6" />
                  </button>
                </div>
              )}

              {/* Cabeçalho */}
              <ConversationHeader
                conversation={conversaSelecionada}
                supervisionMode={modoSupervisao}
                onAssume={canAssumeConversation ? assumirConversa : undefined}
                isAssuming={isAssumingConversation}
                onClose={
                  modoSupervisao
                    ? undefined
                    : () => setModalEncerrarAberto(true)
                }
                onReturnToQueue={
                  modoSupervisao ? undefined : () => devolverConversaParaFila()
                }
                isReturningToQueue={isReturningToQueue}
                onReturnToAi={
                  canReturnConversationToAi
                    ? () => devolverConversaParaIa()
                    : undefined
                }
                isReturningToAi={isReturningToAi}
                onBlock={
                  modoSupervisao
                    ? undefined
                    : () => setModalBloquearAberto(true)
                }
                onSchedule={
                  canScheduleAppointment
                    ? () => setIsSchedulingOpen(true)
                    : undefined
                }
                onCreateProtocol={
                  modoSupervisao
                    ? undefined
                    : () => setIsProtocolModalOpen(true)
                }
                onTemplate={modoSupervisao ? undefined : openTemplateModal}
                isTemplateLoading={isSendingTemplate}
              />

              {modoSupervisao && (
                <div className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700">
                  Modo supervisão ativo (somente leitura).
                </div>
              )}

              {/* Mensagens */}
              <MessageList
                conversationId={conversaSelecionada.id}
                messages={conversaSelecionada.messages}
                previousConversations={conversasAnteriores}
                hasMorePreviousConversations={hasMoreConversationHistory}
                isLoadingPreviousConversations={isConversationHistoryLoading}
                onLoadPreviousConversations={loadPreviousConversations}
              />

              {/* Campo de envio */}
              <MessageInput
                text={novaMensagem}
                setText={setNovaMensagem}
                onSubmit={handleEnviar}
                onUpload={handleUploadArquivo}
                onAudioSend={handleEnviarAudio}
                onAudioError={(message) => error(message)}
                onLocationSend={handleEnviarLocalizacao}
                canSendLocation={canSendEstablishmentLocation}
                disabled={modoSupervisao}
                blocked={isWhatsappWindowExpired}
                blockedReason={whatsappWindowReason}
                isUploadingFile={isUploadingFile}
              />

              <ConversationTemplateModal
                isOpen={isTemplateModalOpen}
                templates={templateOptions}
                selectedTemplateId={selectedTemplateId}
                templatesLoading={templatesLoading}
                isSending={isSendingTemplate}
                onClose={closeTemplateModal}
                onSelectTemplate={setSelectedTemplateId}
                onSend={sendSelectedTemplate}
              />

              {conversaSelecionada && canScheduleAppointment && (
                <ConversationScheduleModal
                  isOpen={isSchedulingOpen}
                  onClose={() => setIsSchedulingOpen(false)}
                  tenant={conversaSelecionada.tenant}
                  userId={conversaSelecionada.user.id ?? ""}
                  conversationId={conversaSelecionada.id}
                />
              )}

              {conversaSelecionada && (
                <ConversationProtocolModal
                  isOpen={isProtocolModalOpen}
                  onClose={() => setIsProtocolModalOpen(false)}
                  conversationId={conversaSelecionada.id}
                />
              )}

              <ConfirmationModal
                isOpen={!!modalEncerrarAberto}
                title="Confirmar encerramento"
                description="Tem certeza que deseja finalizar o atendimento?"
                onCancel={() => setModalEncerrarAberto(false)}
                onConfirm={() => encerrarConversa()}
                isLoading={isClosing}
              />

              <ConfirmationModal
                isOpen={!!modalBloquearAberto}
                title="Bloquear contato"
                description="Esse contato será bloqueado e novas mensagens serão ignoradas. Deseja continuar?"
                onCancel={() => setModalBloquearAberto(false)}
                onConfirm={() => bloquearContato()}
                isLoading={isBlocking}
              />
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-center">
              <p>Selecione uma conversa à esquerda</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
